import { userManagementService } from '@/api'
import router from '@/services/vuerouter/vueRouter'
import gql from 'graphql-tag'
import { onError as onApolloError } from 'apollo-link-error'
import { HttpLink } from 'apollo-link-http'
import { ApolloClient } from 'apollo-client'
import { ApolloLink, Observable } from 'apollo-link'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { createRetryLink } from './retryLink'
import { configureLiveUpdates } from '@/services/liveUpdates/liveUpdates'

function installApollo ({
                          httpLinkConfig = { uri: '/graphql', credentials: 'include' },
                          inMemoryCacheConfig,
                          beforeRequest,
                          refreshAccess,
                          onError
                        }) {
  const errorLink = onError && onApolloError(onError)
  // Before the request link, so a repeated operation gets a refreshed access token
  const retryLink = refreshAccess && createRetryLink({ refreshAccess })
  const requestLink = beforeRequest && createRequestLink(beforeRequest)
  const httpLink = new HttpLink(httpLinkConfig)

  const apollo = new ApolloClient({
    link: ApolloLink.from([errorLink, retryLink, requestLink, httpLink].filter(Boolean)),
    cache: new InMemoryCache(inMemoryCacheConfig),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      },
      mutate: {
        errorPolicy: 'all'
      }
    }
  })
  return apollo
}

function createRequestLink (beforeRequest) {
  return new ApolloLink((operation, forward) =>
    new Observable(observer => {
      let handle
      Promise.resolve(operation)
        .then(oper => beforeRequest(oper))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          })
        })
        .catch(observer.error.bind(observer))

      return () => {
        if (handle) handle.unsubscribe()
      }
    })
  )
}

function isApolloInitialized () {
  if (typeof apollo === 'undefined') {
    return false
  } else {
    return true
  }
}

const graphQlErrorHandler = new function () {
  this.handle = function () {}
}()

const beforeRequest = async operation => {
  await userManagementService.ensureAccess()
  operation.setContext({
    headers: {
      'X-XSRF-TOKEN': userManagementService.getXsrfToken(),
      'Authorization': 'Bearer ' + userManagementService.getAccessToken()
    }
  })
}

const refreshAccess = async () => {
  await userManagementService.refreshRejectedAccess()
  return userManagementService.isAuthenticated() && !userManagementService.isAccessExpired()
}

// Called once the retry link gave up, so the error is not a short hiccup
const onError = error => {
  if (error.networkError) {
    // The refresh token was rejected as well, so the user has to sign in again
    if (error.networkError.statusCode === 401 && !userManagementService.isAuthenticated()) {
      router.push({ name: 'login' }).catch(() => {})
      return
    }
    // A request of a hidden page, e.g. polling in a background tab, must not replace the page
    if (document.visibilityState !== 'visible' || router.currentRoute.name === 'network-error') return
    router.push({ name: 'network-error', query: {
        from: router.currentRoute.query.from || router.currentRoute.fullPath
      } }
    ).catch(() => {})
  } else if (error.graphQLErrors) {
    graphQlErrorHandler.handle(error.graphQLErrors[0])
  }
}

const apollo = installApollo({
  httpLinkConfig: { uri: `${process.env.VUE_APP_API_URL}/graphql`, credentials: 'include' },
  beforeRequest: beforeRequest,
  refreshAccess: refreshAccess,
  onError: onError
})

configureLiveUpdates({
  url: `${process.env.VUE_APP_API_URL}/live-updates`,
  headers: async () => {
    await userManagementService.ensureAccess()
    return {
      'X-XSRF-TOKEN': userManagementService.getXsrfToken(),
      'Authorization': 'Bearer ' + userManagementService.getAccessToken()
    }
  },
  refreshAccess: () => userManagementService.refreshRejectedAccess()
})

function query(queryOptions) {
  return apollo.query(queryOptions)
}

function watchQuery ({ query, variables, fetchPolicy, pollInterval }) {
  if (!isApolloInitialized()) return

  let options = { query, variables }
  fetchPolicy != null && (options['fetchPolicy'] = fetchPolicy)
  pollInterval != null && (options['pollInterval'] = pollInterval)

  return apollo.watchQuery(options)
}

function mutate ({ mutation, variables, update, refetchQueries }) {
  if (!isApolloInitialized()) return

  return apollo.mutate({
    mutation: mutation,
    variables: variables,
    update: update,
    refetchQueries: refetchQueries
  })
}

export default apollo
export { gql, watchQuery, query, mutate }
